import Button from '@/componentsDS/Button/Button';
import { ButtonSize, ButtonType, ButtonVariant } from '@/componentsDS/Button/Button.type';
import Icon from '@/componentsDS/Icon/Icon';
import { IconSize, IconStyle } from '@/componentsDS/Icon/Icon.type';
import classNames from 'classnames';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { FloatingPanelSize, FloatingPanelType } from './FloatingPanel.type';
import FloatingPanelHeader from './FloatingPanelHeader';

const FloatingPanel = forwardRef (({ title, type = FloatingPanelType.STANDARD, size = FloatingPanelSize.LARGE, icon, messages = [], messageHeader, className = '' }, ref) => {
	const [panelExpanded, setPanelExpanded] = useState(false);
	const [panelOpen, setPanelOpen] = useState(true);

	const divRef = useRef(null);

	const panelClass = classNames('DS__floating-panel', {
		'DS__floating-panel--open': panelOpen,
		'DS__floating-panel--closed': !panelOpen,
		'DS__floating-panel--expanded': panelExpanded,
		'DS__floating-panel--collapsed': !panelExpanded,
		[className]: !!className
	});

	const contentClass = classNames('DS__floating-panel__content', {
		'DS__floating-panel__content--lg': size === FloatingPanelSize.LARGE,
		'DS__floating-panel__content--sm': size === FloatingPanelSize.SMALL
	});

	const getMessageClass = (message) =>
		classNames('DS__floating-panel__message', {
			'DS__floating-panel__message--clickable': message.onClick != null
		});

	// Debounced version of toggle expand to prevent ResizeObserver errors
	const debouncedResize = debounce(() => {
		// Using setTimeout with 0 delay to push the state update to the next event loop
		// This helps prevent ResizeObserver loop errors
		setTimeout(() => {
			setPanelExpanded((prev) => !prev);
		}, 0);
	}, 50); // 50ms debounce time

	const onToggleExpand = () => {
		debouncedResize();
	};

	// Clean up the debounce function on unmount
	useEffect(() => {
		return () => {
			debouncedResize.cancel();
		};
	}, [debouncedResize]);

	const onPanelClose = () => {
		setPanelOpen(false);
	};
	const onPanelOpen = () => {
		setPanelOpen(true);
	};

	const onToggleOpen = () => {
		setPanelOpen((prev) => !prev);
	};

	useImperativeHandle(ref, () => ({
		open: onPanelOpen,
		close: onPanelClose
	}));

	const panelIcon = type === FloatingPanelType.DESTRUCTIVE ? <Icon name="exclamation" size={IconSize.MEDIUM} style={IconStyle.SOLID} /> : icon;

	return (
		<div className={panelClass} ref={divRef}>
			<Button type={ButtonType.SOLID_ICON} variant={type === FloatingPanelType.DESTRUCTIVE ? ButtonVariant.DESTRUCTIVE : ButtonVariant.STANDARD} addbtnClass="DS__floating-panel__button" text={panelIcon} tooltipText={panelOpen ? "Close errors" : "Expand errors"} size={size === FloatingPanelSize.LARGE ? ButtonSize.extraLarge : ButtonSize.large} onClick={onToggleOpen} />

			<div className={contentClass}>
				<FloatingPanelHeader title={title} onToggleExpand={onToggleExpand} onPanelClose={onPanelClose} expanded={panelExpanded} icon={panelIcon} type={type} />
				{/* TO-DO: This message list should to be moved to a separate component */}
				<div className="DS__floating-panel__body">
					{messageHeader != null && <div className="DS__floating-panel__message-header">{messageHeader}</div>}
					{messages.map((message, index) => (
						<div key={index} className={getMessageClass(message)} onClick={message.onClick}>
							{message.message}
						</div>
					))}
				</div>
			</div>
		</div>
	);
});

FloatingPanel.displayName = 'FloatingPanel';

FloatingPanel.propTypes = {
	title: PropTypes.string,
	type: PropTypes.oneOf(['STANDARD', 'DESTRUCTIVE']),
	size: PropTypes.oneOf(['LARGE', 'SMALL']),
	icon: PropTypes.node,
	messages: PropTypes.arrayOf(
		PropTypes.shape({
			message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
			onClick: PropTypes.func
		})
	),
	messageHeader: PropTypes.node,
	className: PropTypes.string
};

export default FloatingPanel;
